import { useRef, useState } from 'react';
import { useTranslation } from 'next-i18next/pages';
import { Popover, PopoverRoot } from '@pkgs/components';
import { Link } from '@ev-tech/ui-core-components/Link';
import { IconButton } from '@ev-tech/ui-core-components/IconButton';

import { replaceURLParams } from '@/utils/utmParams';
import { isTouchDevice } from '@/utils/isTouchDevice';
import { TRANSLATION_NAMESPACE } from '@/i18n/config';
import { useExposeTracking } from '@/hooks/useExposeTracking';
import { ShareIcon } from '@/components/common/Icons/ShareIcon';

import { COPY_LINK_QUERY_PARAM, EXPOSE_SHARING_QUERY_PARAM, PRESERVED_SHARE_QUERY_PARAMS, ShareDialog } from '../ShareDialog/ShareDialog';

import styles from './ShareButton.module.css';

type ShareButtonProps = {
  className?: string;
  usesLinkComponent?: boolean;
  size?: 'small' | 'medium';
};

export const ShareButton = ({ className, usesLinkComponent, size = 'small' }: ShareButtonProps) => {
  const { t } = useTranslation([TRANSLATION_NAMESPACE.watchlist]);
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const { t: tExpose } = useTranslation(TRANSLATION_NAMESPACE.property_expose);
  const { trackExposeUserInteractionEvent } = useExposeTracking();

  const share = () => {
    trackExposeUserInteractionEvent({
      elementId: 'share-expose',
    });

    const url = replaceURLParams(
      location.href,
      {
        utm_source: EXPOSE_SHARING_QUERY_PARAM,
        utm_medium: COPY_LINK_QUERY_PARAM,
      },
      PRESERVED_SHARE_QUERY_PARAMS,
    );

    const payload = {
      title: document.title,
      text: tExpose('search.expose.share.emailBody', { urlWithUTMParams: '' }),
      url,
    };

    // oxlint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (isTouchDevice() && navigator.share && navigator.canShare?.(payload)) {
      // catch because promise rejects if user cancels share
      navigator.share(payload).catch(() => null);
    } else {
      setIsOpen(true);
    }
  };

  return (
    <PopoverRoot open={isOpen} modal="trap-focus" onOpenChange={setIsOpen}>
      <div className={styles.shareButtonWrapper} ref={triggerRef}>
        {usesLinkComponent ? (
          <Link
            aria-expanded={isOpen}
            aria-haspopup="dialog"
            className={className}
            data-testid="expose-share-button"
            icon={<ShareIcon />}
            label={t('search.watchlist.header.share')}
            onClick={share}
            as="button"
            size={size}
          />
        ) : (
          <IconButton
            aria-expanded={isOpen}
            aria-haspopup="dialog"
            className={className}
            data-testid="expose-share-button"
            icon={<ShareIcon />}
            label={t('search.watchlist.header.share')}
            onClick={share}
            size={size}
            variant="primaryAlt"
          />
        )}
        <Popover aria-label={tExpose('search.expose.share.title')} align="end" anchor={triggerRef} className={styles.sharePopover}>
          <ShareDialog onClose={() => setIsOpen(false)} />
        </Popover>
      </div>
    </PopoverRoot>
  );
};
